import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useData } from "@/lib/data";
import { ArrowLeft, Phone, MapPin, Printer, User, Truck } from "lucide-react";

const sum = (items, f) => items.reduce((a, b) => a + (b[f] || 0), 0);

const Metric = ({ label, value, cls, testId }) => (
  <div className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-4">
    <p className="text-[10px] font-mono uppercase tracking-widest text-[#6B5B4A]">{label}</p>
    <p className={`font-display text-3xl font-extrabold tabular-nums ${cls}`} data-testid={testId}>{value}</p>
  </div>
);

export default function CustomerDetail() {
  const { id } = useParams();
  const { customers, productById } = useData();
  const [orders, setOrders] = useState([]);
  const [shipments, setShipments] = useState([]);
  const customer = customers.find((c) => c.id === id);

  useEffect(() => {
    Promise.all([api.get("/orders"), api.get("/shipments")]).then(([o, s]) => {
      setOrders(o.data.filter((x) => x.customer_id === id));
      setShipments(s.data.filter((x) => x.customer_id === id));
    });
  }, [id]);

  const ordered = orders.reduce((a, o) => a + sum(o.items, "qty"), 0);
  const shipped = orders.reduce((a, o) => a + sum(o.items, "shipped_qty"), 0);

  return (
    <div className="p-5 lg:p-10 max-w-[1400px]" data-testid="customer-detail-page">
      <Link to="/musteri" className="inline-flex items-center gap-2 text-sm text-[#6B5B4A] hover:text-[#B45309] mb-4" data-testid="customer-detail-back">
        <ArrowLeft className="w-4 h-4" /> Müşteriler
      </Link>
      <p className="text-[11px] font-mono uppercase tracking-widest text-[#B45309] mb-2">Müşteri Geçmişi</p>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight" data-testid="customer-detail-name">
        {customer?.name || "…"}
      </h1>
      <div className="flex flex-wrap gap-4 text-sm text-[#6B5B4A] mt-2 mb-6">
        {customer?.contact_name && <span className="flex items-center gap-1" data-testid="cd-contact"><User className="w-3 h-3" />{customer.contact_name}</span>}
        {customer?.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{customer.phone}</span>}
        {customer?.cargo_company && <span className="flex items-center gap-1" data-testid="cd-cargo"><Truck className="w-3 h-3" />{customer.cargo_company}</span>}
        {customer?.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{customer.address}</span>}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Metric label="Sipariş Sayısı" value={orders.length} cls="" testId="cd-order-count" />
        <Metric label="Sipariş Edilen" value={ordered} cls="" testId="cd-ordered" />
        <Metric label="Sevk Edilen" value={shipped} cls="text-[#047857]" testId="cd-shipped" />
        <Metric label="Kalan" value={ordered - shipped} cls="text-[#B45309]" testId="cd-remaining" />
      </div>

      <h2 className="font-display text-xl font-bold uppercase tracking-tight mb-3">Siparişler</h2>
      <div className="space-y-3 mb-8">
        {orders.length === 0 && <p className="text-sm text-[#8C7B68]">Bu müşterinin siparişi yok.</p>}
        {orders.map((o) => (
          <div key={o.id} className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-4 overflow-x-auto" data-testid={`cd-order-${o.id}`}>
            <p className="text-[11px] font-mono uppercase tracking-widest text-[#6B5B4A] mb-2">
              Teslim: {o.delivery_date} · Alındı: {new Date(o.created_at).toLocaleDateString("tr-TR")}
            </p>
            <table className="w-full text-sm zs-table">
              <thead><tr>
                <th className="text-left py-1">Model</th><th className="text-left py-1">Renk</th>
                <th className="text-center py-1">N°</th><th className="text-right py-1">Adet</th>
                <th className="text-right py-1">Sevk</th><th className="text-right py-1">Kalan</th>
              </tr></thead>
              <tbody>
                {o.items.map((it) => {
                  const p = productById(it.product_id);
                  return (
                    <tr key={`${it.product_id}-${it.size}`} className="border-t border-[#E8DDCA]">
                      <td className="py-1.5 font-mono text-[#B45309]">{p?.model_code || "—"}</td>
                      <td className="py-1.5 text-[#6B5B4A]">{p?.model_color || "—"}</td>
                      <td className="py-1.5 text-center font-mono">{it.size}</td>
                      <td className="py-1.5 text-right font-mono">{it.qty}</td>
                      <td className="py-1.5 text-right font-mono text-[#047857]">{it.shipped_qty || 0}</td>
                      <td className="py-1.5 text-right font-mono font-bold text-[#B45309]">{it.qty - (it.shipped_qty || 0)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <h2 className="font-display text-xl font-bold uppercase tracking-tight mb-3">Sevkiyatlar</h2>
      <div className="space-y-2">
        {shipments.length === 0 && <p className="text-sm text-[#8C7B68]">Henüz sevkiyat yok.</p>}
        {shipments.map((s) => (
          <div key={s.id} className="flex items-center justify-between rounded-md border border-[#DCCFB8] bg-[#FFFBF4] px-4 py-3">
            <span className="text-sm font-mono text-[#6B5B4A]">{new Date(s.created_at).toLocaleString("tr-TR")}</span>
            <div className="flex items-center gap-4">
              <span className="font-mono font-bold text-[#047857]">{sum(s.items, "qty")} çift</span>
              <a href={`/irsaliye/${s.id}`} target="_blank" rel="noreferrer" data-testid={`cd-waybill-${s.id}`}
                className="flex items-center gap-1 text-xs text-[#B45309] hover:underline"><Printer className="w-3 h-3" /> İrsaliye</a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
